import React, { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Row, Col, Card, CardBody, Table, Input, Button, Modal, ModalHeader, ModalBody, FormGroup, Label } from "reactstrap";
import { formatPriceWithDollar } from "utils/currency";
import { formatDateForDisplay, formatDateTimeForDisplay } from "utils/dateUtils";

const BalancesTab = ({ data }) => {
  const { t } = useTranslation();


  // Mock data/placeholders based on screenshot to show the design
  // User will provide actual mappings later
  const balancesInfo = useMemo(() => {
    const formatUS = (val) => val && val !== '-' ? formatDateForDisplay(val).replace(/-/g, '/') : val;
    const formatUSTime = (val) => val && val !== '-' ? formatDateTimeForDisplay(val).replace(/-/g, '/') : val;

    const asIdmsOrFormat = (val, fmt) => {
      if (!val || val === "-") return null;
      const s = String(val).trim();
      if (/^\d{1,2}\/\d{1,2}\/\d{4}/.test(s)) return s;
      return fmt(s);
    };

    return {
      bookedDate: asIdmsOrFormat(data?.booked_date, formatUSTime) || formatUSTime(data?.createdAt) || "-",
      contractedDate: formatUS(data?.worksheet?.contract_date) || "-",
      lastTransactionDate: asIdmsOrFormat(data?.last_transaction_date, formatUSTime) || "-",
      lastModified: formatUSTime(data?.updatedAt) || "-",
      lastPaymentDate: asIdmsOrFormat(data?.last_payment_date, formatUSTime) || "-",
      creationMethod: data?.creation_method || "DTD Sale",
      contract: data?.worksheet?.contract_name || "1491 - Retail Install Cont_TX-BPC_3-25_TDE-TD_IDMS",
      loanRate: data?.worksheet?.loan_rate ? `${data.worksheet.loan_rate}%` : "18.0000%",
      regZapr: data?.worksheet?.apr ? `${data.worksheet.apr}%` : "16.7597%",
      firstPaymentDate: formatUS(data?.worksheet?.schedules?.[0]?.first_payment_date) || "02/20/2026",
      origMaturityDate: formatUS(data?.worksheet?.schedule?.[0]?.payment_date) || "06/22/2029",
      autoPay: data?.auto_pay || "OFF"
    };
  }, [data]);

  // Theme detection logic matching other tabs
  const [isDarkMode, setIsDarkMode] = useState(() => {
    const darkMode = localStorage.getItem("darkMode");
    return darkMode ? JSON.parse(darkMode) : true;
  });

  const [calcDrawerVisible, setCalcDrawerVisible] = useState(false);
  const [calcDrawerOpen, setCalcDrawerOpen] = useState(false);

  const openCalcDrawer = () => {
    setCalcDrawerVisible(true);
    setTimeout(() => setCalcDrawerOpen(true), 10);
  };

  const closeCalcDrawer = () => {
    setCalcDrawerOpen(false);
    setTimeout(() => setCalcDrawerVisible(false), 300);
  };

  useEffect(() => {
    const interval = setInterval(() => {
      const darkMode = localStorage.getItem("darkMode");
      const newTheme = darkMode ? JSON.parse(darkMode) : true;
      if (newTheme !== isDarkMode) {
        setIsDarkMode(newTheme);
      }
    }, 800);
    return () => clearInterval(interval);
  }, [isDarkMode]);

  const colors = useMemo(() => {
    return {
      containerBg: isDarkMode ? "transparent" : "#ffffff",
      cardBg: isDarkMode ? "#1e1e2f" : "#f4f7f6",
      textColor: isDarkMode ? "#ffffff" : "#313335",
      mutedText: isDarkMode ? "rgba(255,255,255,0.6)" : "#6c757d",
      borderColor: isDarkMode ? "rgba(255,255,255,0.1)" : "#e3e3e3",
      tableHeaderBg: isDarkMode ? "#1a1a2e" : "#e9ecef",
      totalRowBg: isDarkMode ? "rgba(255,255,255,0.03)" : "#f1f3f5",
      increaseColor: isDarkMode ? "#2dce89" : "#218838",
      decreaseColor: isDarkMode ? "#f5365c" : "#bd2130"
    };
  }, [isDarkMode]);


  const tableRows = [
    { label: "Down Payment", original: data?.balances?.original_def_downs, increase: 0, decrease: data?.balances?.decrease_def_down || 0, collected: data?.balances?.collected_def_downs, current: data?.balances?.current_def_down },
    { label: "Principal", original: data?.balances?.original_principal, increase: 0, decrease: data?.balances?.decrease_principal || 0, collected: data?.balances?.collected_principal, current: data?.balances?.current_principal },
    { label: "Non Earning Principal", original: data?.balances?.original_non_earning_principal || 0, increase: data?.balances?.increase_non_earning_principal || 0, decrease: data?.balances?.decrease_non_earning_principal || 0, collected: data?.balances?.collected_non_earning_principal, current: data?.balances?.current_non_earning_principal || 0 },
    { label: "Sales Tax Balance", original: data?.balances?.original_state_sales_tax, increase: 0, decrease: data?.balances?.decrease_state_sales_tax || 0, collected: data?.balances?.collected_sales_tax ?? data?.balances?.collected_taxes, current: data?.balances?.current_state_sales_tax },
    { label: "Unearned Interest", original: data?.balances?.original_unearned_interest, increase: 0, decrease: data?.balances?.decrease_unearned_interest || 0, collected: data?.balances?.collected_unearned_interest ?? data?.balances?.collected_accrued_interest, current: data?.balances?.current_unearned_interest || 0 },
    { label: "Accrued Interest", original: 0, increase: data?.balances?.increase_accrued_interest, decrease: data?.balances?.decrease_accrued_interest, collected: data?.balances?.collected_accrued_interest, current: data?.balances?.current_accrued_interest },
    { label: "Late Fees", original: 0, increase: data?.balances?.increase_late_fee, decrease: data?.balances?.decrease_late_fee || 0, collected: data?.balances?.collected_late_fee, current: data?.balances?.current_late_fee },
    { label: "Misc Fees", original: 0, increase: data?.balances?.increase_misc_fees, decrease: data?.balances?.decrease_misc_fees || 0, collected: data?.balances?.collected_misc_fees, current: data?.balances?.current_misc_fees },
    { label: "Totals", original: data?.balances?.original_total, increase: data?.balances?.increase_total, decrease: data?.balances?.decrease_total, collected: data?.balances?.collected_total, current: data?.balances?.current_total, isTotal: true },
    { label: "Discount Principal", original: 0, increase: 0, decrease: 0, collected: 0, current: 0, isDiscount: true },
  ];

  return (
    <div className="balances-tab-container p-3" style={{ backgroundColor: colors.containerBg, color: colors.textColor, minHeight: "100%", borderRadius: "0" }}>
      <div className="d-flex justify-content-between align-items-center mb-3">
        <h4 className="mb-0 font-weight-bold" style={{ color: colors.textColor, fontSize: "1.1rem" }}>BALANCES</h4>
        <div style={{ width: "200px" }}>
          <Input
            type="select"
            name="loanSelect"
            id="loanSelect"
            size="sm"
            style={{
              backgroundColor: isDarkMode ? "#1e1e2f" : "#fff",
              borderColor: colors.borderColor,
              color: colors.textColor
            }}
          >
            <option>Primary Loan</option>
          </Input>
        </div>
      </div>

      <Card className="mb-4 border-0 shadow-sm" style={{ backgroundColor: colors.cardBg, borderRadius: "0px" }}>
        <CardBody className="p-3">
          <Row>
            {/* Column 1: Dates & Contract */}
            <Col md="4" style={{ borderRight: `1px solid ${colors.borderColor}` }}>
              <div className="mb-2">
                <small className="d-block p-0" style={{ color: colors.mutedText }}>Booked Date:</small>
                <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.bookedDate}</div>
              </div>
              <div className="mb-2">
                <small className="d-block p-0" style={{ color: colors.mutedText }}>Contracted Date:</small>
                <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.contractedDate}</div>
              </div>
              <div className="mb-2">
                <small className="d-block p-0" style={{ color: colors.mutedText }}>Last Transaction Date:</small>
                <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.lastTransactionDate}</div>
              </div>
              <div className="mt-4">
                <small className="d-block p-0" style={{ color: colors.mutedText }}>Contract:</small>
                <div style={{ fontSize: "0.85rem", fontWeight: "700" }}>
                  {balancesInfo.contract}
                  <Button outline color="info" size="sm" className="ml-2 py-1 px-2 mt-1 font-weight-bold" style={{ fontSize: "0.65rem", verticalAlign: "middle", display: "inline-block" }} onClick={openCalcDrawer}>
                    VIEW CALC OPTIONS
                  </Button>
                </div>
              </div>
            </Col>

            {/* Column 2: Modified & Method */}
            <Col md="3" style={{ borderRight: `1px solid ${colors.borderColor}` }}>
              <div className="mb-2">
                <small className="d-block p-0" style={{ color: colors.mutedText }}>Last Modified:</small>
                <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.lastModified}</div>
              </div>
              <div className="mb-2">
                <small className="d-block p-0" style={{ color: colors.mutedText }}>Last Payment Date:</small>
                <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.lastPaymentDate}</div>
              </div>
              <div className="mb-2">
                <small className="d-block p-0" style={{ color: colors.mutedText }}>Creation Method:</small>
                <div style={{ fontSize: "0.9rem", fontWeight: "600", color: "#1d8cf8" }}>{balancesInfo.creationMethod}</div>
                <small
                  className="d-block"
                  style={{ fontSize: "0.8rem", color: "#1d8cf8", cursor: "pointer" }}
                  onClick={() => {
                    const dealId = data?.worksheet?.deal?.deal_id
                    const worksheetId = data?.worksheet?.worksheet_id;
                    if (dealId && worksheetId) {
                      window.open(`/deals/${dealId}?worksheet=${worksheetId}`, '_blank');
                    }
                  }}
                >
                  Click Here For Original Deal Details
                </small>
                <small
                  className="d-block"
                  style={{ fontSize: "0.8rem", color: "#1d8cf8", cursor: "pointer" }}
                  onClick={() => {
                    const dealId = data?.worksheet?.deal?.deal_id
                    const worksheetId = data?.worksheet?.worksheet_id;
                    if (dealId && worksheetId) {
                      window.open(`/deals/${dealId}?worksheet=${worksheetId}`, '_blank');
                    }
                  }}
                >
                  ({data?.worksheet?.deal?.deal_id})
                </small>
              </div>
            </Col>

            {/* Column 3: Loan Details */}
            <Col md="3" style={{ borderRight: `1px solid ${colors.borderColor}` }}>
              <Row>
                <Col md="6" className="mb-2">
                  <small className="d-block p-0" style={{ color: colors.mutedText }}>Loan Rate</small>
                  <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.loanRate}</div>
                </Col>
                {/* <Col md="6" className="mb-2">
                  <small className="d-block p-0" style={{ color: colors.mutedText }}>REG Z APR</small>
                  <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.regZapr}</div>
                </Col> */}
                <Col md="6" className="mb-2">
                  <small className="text-muted d-block p-0" style={{ color: colors.mutedText }}>First Payment Date:</small>
                  <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.firstPaymentDate}</div>
                </Col>
                <Col md="6" className="mb-2">
                  <small className="text-muted d-block p-0" style={{ color: colors.mutedText }}>Orig Maturity Date</small>
                  <div style={{ fontSize: "0.9rem", fontWeight: "600" }}>{balancesInfo.origMaturityDate}</div>
                </Col>
              </Row>
            </Col>

            {/* Column 4: EPS */}
            <Col md="2">
              <div style={{ fontWeight: "700", fontSize: "1rem" }}>EPS</div>
              <div className="mt-2" style={{ fontSize: "0.85rem" }}>
                Auto Payment is <span style={{ color: "#fd5d93", fontWeight: "700" }}>TURNED OFF</span> for this balance.
              </div>
              <Button outline size="sm" color="info" className="mt-3 font-weight-bold" style={{ fontSize: "0.75rem", display: "flex", alignItems: "center", gap: "5px" }}>
                <i className="tim-icons icon-settings-gear-63" style={{ fontSize: "12px" }} /> UPDATE AUTOPAY
              </Button>
            </Col>
          </Row>
        </CardBody>
      </Card>

      <div className="balances-table-container shadow-sm p-0 mb-4" style={{ backgroundColor: colors.cardBg, borderRadius: "0px", overflow: "hidden" }}>
        <Table borderless responsive className="mb-0" style={{ fontSize: "0.85rem", color: colors.textColor }}>
          <thead style={{ backgroundColor: colors.tableHeaderBg, borderBottom: `1px solid ${colors.borderColor}` }}>
            <tr className="text-center font-weight-bold" style={{ color: colors.textColor }}>
              <th className="text-left" style={{ width: "20%" }}></th>
              <th style={{ width: "16%" }}>Original Balance</th>
              <th style={{ width: "16%", color: colors.increaseColor }}>Increase</th>
              <th style={{ width: "16%", color: colors.decreaseColor }}>Decrease</th>
              <th style={{ width: "16%" }}>Collected</th>
              <th style={{ width: "16%" }}>Current Balance</th>
            </tr>
          </thead>
          <tbody>
            {tableRows.map((row, idx) => {
              const rowBg = row.isTotal ? colors.totalRowBg : (idx % 2 === 0 ? "transparent" : (isDarkMode ? "rgba(255,255,255,0.05)" : "#f9f9f9"));
              const labelBg = isDarkMode ? "rgba(255,255,255,0.08)" : "#eeeeee";

              return (
                <tr key={idx} className="text-center" style={{
                  borderBottom: `1px solid ${colors.borderColor}`,
                  fontWeight: row.isTotal ? "700" : "400",
                  backgroundColor: rowBg,
                }}>
                  <td className="text-left pl-3" style={{
                    fontWeight: "600",
                    color: colors.textColor,
                    backgroundColor: labelBg,
                    borderRight: `1px solid ${colors.borderColor}`,
                    width: "20%"
                  }}>
                    {row.label}
                  </td>
                  <td>{["Misc Fees", "Late Fees", "Non Earning Principal", "Accrued Interest"].includes(row.label) ? "-" : formatPriceWithDollar(row.original)}</td>
                  <td style={{ color: row.increase > 0 ? "#00f2c3" : "inherit" }}>{formatPriceWithDollar(row.increase)}</td>
                  <td style={{ color: row.decrease > 0 ? "#fd5d93" : "inherit" }}>{formatPriceWithDollar(row.decrease)}</td>
                  <td>{formatPriceWithDollar(row.collected)}</td>
                  <td style={{ fontWeight: "700" }}>{formatPriceWithDollar(row.current)}</td>
                </tr>
              );
            })}
          </tbody>
        </Table>
      </div>

      {/* Payment Terms Summary Table */}
      <div className="d-flex justify-content-center mt-3">
        <div style={{ width: "fit-content", border: `1px solid ${colors.borderColor}`, borderRadius: "0px", overflow: "hidden" }}>
          <Table borderless size="sm" className="mb-0 text-center" style={{ fontSize: "0.75rem", color: colors.textColor }}>
            <thead style={{ backgroundColor: colors.tableHeaderBg, borderBottom: `1px solid ${colors.borderColor}` }}>
              <tr style={{ fontWeight: "700" }}>
                <th className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}>First Pmt Date</th>
                <th className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}># Payments</th>
                <th className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}>Frequency</th>
                <th className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}>Amount</th>
                <th className="px-3 py-2">Final Pmt Amt</th>
              </tr>
            </thead>
            <tbody>
              <tr>
                <td className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}>
                  {formatDateForDisplay(data?.worksheet?.schedules?.[0]?.first_payment_date)}
                </td>
                <td className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}>
                  {data?.worksheet?.schedules?.[0]?.number_of_payments}
                </td>
                <td className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}>
                  {data?.worksheet?.schedules?.[0]?.frequency}
                </td>
                <td className="px-3 py-2 border-right" style={{ borderRight: `1px solid ${colors.borderColor}` }}>
                  {formatPriceWithDollar(data?.worksheet?.schedules?.[0]?.regular_payment)}
                </td>
                <td className="px-3 py-2">
                  {formatPriceWithDollar(data?.worksheet?.schedules?.[0]?.final_payment_amount)}
                </td>
              </tr>
            </tbody>
          </Table>
        </div>
      </div>

      <style>
        {`
          .balances-tab-container small {
            font-size: 0.75rem;
            margin-bottom: 2px;
          }
          .balances-tab-container .table td {
            padding: 0.6rem 0.75rem;
            vertical-align: middle;
          }
          .balances-tab-container .table thead th {
            padding: 0.75rem;
            text-transform: none;
            font-size: 0.85rem;
          }
        `}
      </style>

      {calcDrawerVisible && (
        <>
          <div
            style={{
              position: "fixed",
              top: 0,
              left: 0,
              width: "100vw",
              height: "100vh",
              backgroundColor: "rgba(0,0,0,0.35)",
              zIndex: 2000,
              opacity: calcDrawerOpen ? 1 : 0,
              transition: "opacity 300ms ease",
            }}
            onClick={closeCalcDrawer}
          />
          <div
            style={{
              position: "fixed",
              top: 0,
              right: 0,
              width: "40vw",
              minWidth: "350px",
              height: "100vh",
              backgroundColor: colors.cardBg,
              color: colors.textColor,
              boxShadow: "-8px 0 24px rgba(0,0,0,0.4)",
              zIndex: 2001,
              display: "flex",
              flexDirection: "column",
              transform: calcDrawerOpen ? "translateX(0)" : "translateX(100%)",
              transition: "transform 300ms ease",
            }}
          >
            <div
              style={{
                padding: "16px 24px",
                backgroundColor: "#1d8cf8",
                color: "white",
                display: "flex",
                alignItems: "center",
                justifyContent: "space-between",
                borderBottom: `1px solid ${colors.borderColor}`
              }}
            >
              <h4 className="mb-0" style={{ color: "white", fontSize: "1.1rem", fontWeight: "600" }}>CONTRACT CALC OPTIONS</h4>
              <button
                onClick={closeCalcDrawer}
                style={{
                  background: "transparent",
                  border: "none",
                  color: "white",
                  fontSize: "1.5rem",
                  lineHeight: 1,
                  cursor: "pointer"
                }}
              >
                &times;
              </button>
            </div>
            <div style={{ padding: "24px", overflowY: "auto", flex: 1 }}>
              <Row>
                <Col md="12">
                  <FormGroup>
                    <Label style={{ fontWeight: '600', color: colors.mutedText, fontSize: '0.75rem', marginBottom: '2px' }}>Contract Name:</Label>
                    <Input type="text" value="Retail Install Cont_TX-BPC_3-25_TDE-TD_IDMS" disabled style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f4f4f4', color: colors.textColor, borderColor: colors.borderColor, height: '34px', fontSize: '0.85rem' }} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label style={{ fontWeight: '600', color: colors.mutedText, fontSize: '0.75rem', marginBottom: '2px' }}>Calc Days in Year:</Label>
                    <Input type="text" value="365" disabled style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f4f4f4', color: colors.textColor, borderColor: colors.borderColor, height: '34px', fontSize: '0.85rem' }} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label style={{ fontWeight: '600', color: colors.mutedText, fontSize: '0.75rem', marginBottom: '2px' }}>Allow Late Fees:</Label>
                    <Input type="text" value="True" disabled style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f4f4f4', color: colors.textColor, borderColor: colors.borderColor, height: '34px', fontSize: '0.85rem' }} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label style={{ fontWeight: '600', color: colors.mutedText, fontSize: '0.75rem', marginBottom: '2px' }}>Late Fee Minimum:</Label>
                    <Input type="text" value="$0.00" disabled style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f4f4f4', color: colors.textColor, borderColor: colors.borderColor, height: '34px', fontSize: '0.85rem' }} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label style={{ fontWeight: '600', color: colors.mutedText, fontSize: '0.75rem', marginBottom: '2px' }}>Late Fee Maximum:</Label>
                    <Input type="text" value="$9,999,999.00" disabled style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f4f4f4', color: colors.textColor, borderColor: colors.borderColor, height: '34px', fontSize: '0.85rem' }} />
                  </FormGroup>
                </Col>
              </Row>
              <Row>
                <Col md="6">
                  <FormGroup>
                    <Label style={{ fontWeight: '600', color: colors.mutedText, fontSize: '0.75rem', marginBottom: '2px' }}>Late Fee Days:</Label>
                    <Input type="text" value={data?.worksheet?.inventory?.location?.sales_location?.lender_defaults?.late_fee_days} disabled style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f4f4f4', color: colors.textColor, borderColor: colors.borderColor, height: '34px', fontSize: '0.85rem' }} />
                  </FormGroup>
                </Col>
                <Col md="6">
                  <FormGroup>
                    <Label style={{ fontWeight: '600', color: colors.mutedText, fontSize: '0.75rem', marginBottom: '2px' }}>Late Fee Percentage:</Label>
                    <Input type="text" value={data?.worksheet?.inventory?.location?.sales_location?.lender_defaults?.late_fee_percentage + "%"} disabled style={{ backgroundColor: isDarkMode ? 'rgba(255,255,255,0.05)' : '#f4f4f4', color: colors.textColor, borderColor: colors.borderColor, height: '34px', fontSize: '0.85rem' }} />
                  </FormGroup>
                </Col>
              </Row>
            </div>
          </div>
        </>
      )}
    </div>
  );
};

export default BalancesTab;
